/**
 * PAX — PwaInstallCard
 *
 * Karta w /settings pozwalająca użytkownikowi:
 *  1) Zainstalować PWA (Android/iOS/Desktop) — istniejąca funkcja.
 *  2) RĘCZNIE sprawdzić dostępność nowej wersji aplikacji przez tapnięcie
 *     całego kafelka (wykorzystuje istniejący mechanizm workbox — nie tworzy
 *     równoległego systemu aktualizacji).
 *
 * Stany karty (instalacja):
 *  - installed:      "Aplikacja PAX jest zainstalowana" (green check)
 *  - installable:    button "Zainstaluj PAX"
 *  - ios-manual:     instrukcja Share → "Do ekranu początkowego"
 *  - dismissed:      "Oczekiwanie na dostępność"
 *  - suppressed:     na ścieżce OAuth
 *  - unsupported:    "Twoja przeglądarka nie wspiera instalacji"
 *
 * Stany karty (ręczne sprawdzanie aktualizacji):
 *  - idle:      podpowiedź "Tapnij, aby sprawdzić aktualizacje"
 *  - checking:  "Sprawdzanie aktualizacji..."
 *  - uptodate:  "Masz zainstalowaną najnowszą wersję aplikacji." + timestamp
 *  - available: "Dostępna jest nowa wersja aplikacji." + przycisk "Aktualizuj teraz"
 *
 * Cały kafelek jest klikalny. Kliknięcie wywołuje sprawdzenie aktualizacji
 * (nawet w stanie `installed` — user chce wiedzieć czy jest coś nowego).
 * Klik nie inicjuje sprawdzenia gdy user tapa bezpośrednio w przycisk
 * "Zainstaluj PAX" / "Aktualizuj teraz" (stopPropagation).
 */
import { Download, Share, Plus, CheckCircle2, Info, RefreshCw, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePwaInstall } from '@/pwa/usePwaInstall';
import { usePwaUpdateCheck } from '@/pwa/usePwaUpdateCheck';

function formatLastChecked(ts) {
  if (!ts) return null;
  try {
    const d = new Date(ts);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
      d.getHours(),
    )}:${pad(d.getMinutes())}`;
  } catch {
    return null;
  }
}

export default function PwaInstallCard() {
  const { state, promptInstall } = usePwaInstall();
  const { updateStatus, lastCheckedAt, checkForUpdates, activate } = usePwaUpdateCheck();

  const isChecking = updateStatus === 'checking';
  const lastCheckedLabel = formatLastChecked(lastCheckedAt);

  const handleCardClick = () => {
    // Nie dubluj sprawdzenia gdy trwa
    if (isChecking) return;
    checkForUpdates();
  };

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <Card
      data-testid="pwa-install-card"
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
      className="border border-gray-200 hover:border-[#2563EB] transition-colors cursor-pointer select-none"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
            <Download className="w-5 h-5 text-[#2563EB]" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base text-[#0F1F3D]">
              Zainstaluj aplikację
            </CardTitle>
            <CardDescription>
              Uruchom PAX jak natywną aplikację — bez paska przeglądarki, z ekranu głównego telefonu.
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 space-y-3">
        {/* Sekcja stanu instalacji */}
        {state === 'installed' && (
          <div
            data-testid="pwa-install-card-installed"
            className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-md px-3 py-2"
          >
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            <span>Aplikacja PAX jest zainstalowana i uruchomiona w trybie natywnym.</span>
          </div>
        )}

        {state === 'installable' && (
          <div className="flex justify-end" onClick={stopPropagation}>
            <Button
              type="button"
              onClick={() => promptInstall()}
              data-testid="pwa-install-card-install-btn"
              className="bg-[#2563EB] hover:bg-[#1E4FCC] text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Zainstaluj PAX
            </Button>
          </div>
        )}

        {state === 'dismissed' && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">
              Wcześniej odrzuciłeś propozycję instalacji. Możesz ją uruchomić ponownie tutaj gdy pojawi się w przeglądarce.
            </p>
            <div className="flex justify-end" onClick={stopPropagation}>
              <Button
                type="button"
                disabled
                variant="outline"
                data-testid="pwa-install-card-install-btn"
                className="text-gray-400 cursor-not-allowed"
              >
                <Download className="w-4 h-4 mr-2" />
                Oczekiwanie na dostępność
              </Button>
            </div>
          </div>
        )}

        {state === 'ios-manual' && (
          <div className="space-y-2 text-sm text-gray-700">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-50 text-[#2563EB] text-xs font-semibold flex items-center justify-center flex-shrink-0">1</span>
              <Share className="w-4 h-4 text-[#2563EB] flex-shrink-0" />
              <span>Kliknij ikonę <b>Udostępnij</b> na dole ekranu Safari</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-50 text-[#2563EB] text-xs font-semibold flex items-center justify-center flex-shrink-0">2</span>
              <Plus className="w-4 h-4 text-[#2563EB] flex-shrink-0" />
              <span>Wybierz <b>Do ekranu początkowego</b> i potwierdź</span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              PAX pojawi się na ekranie głównym Twojego iPhone / iPada. Otwieraj ją stąd — działa w trybie natywnym.
            </p>
          </div>
        )}

        {state === 'suppressed' && (
          <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-md px-3 py-2">
            <Info className="w-4 h-4 flex-shrink-0" />
            <span>Instalacja tymczasowo niedostępna (trwa logowanie).</span>
          </div>
        )}

        {state === 'unsupported' && (
          <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-md px-3 py-2">
            <Info className="w-4 h-4 flex-shrink-0" />
            <span>
              Twoja przeglądarka nie wspiera instalacji PWA. Użyj Chrome, Edge, Samsung Internet (Android) lub Safari (iOS).
            </span>
          </div>
        )}

        {/* Sekcja ręcznego sprawdzania aktualizacji — widoczna niezależnie od stanu instalacji */}
        <div className="border-t border-gray-100 pt-3">
          {isChecking && (
            <div
              data-testid="pwa-update-checking"
              className="flex items-center gap-2 text-sm text-gray-600"
            >
              <RefreshCw className="w-4 h-4 flex-shrink-0 animate-spin" />
              <span>Sprawdzanie aktualizacji...</span>
            </div>
          )}

          {!isChecking && updateStatus === 'available' && (
            <div className="space-y-2" data-testid="pwa-update-available">
              <div className="flex items-center gap-2 text-sm text-[#0F1F3D] bg-blue-50 rounded-md px-3 py-2">
                <Sparkles className="w-4 h-4 flex-shrink-0 text-[#2563EB]" />
                <span>Dostępna jest nowa wersja aplikacji.</span>
              </div>
              <div className="flex justify-end" onClick={stopPropagation}>
                <Button
                  type="button"
                  onClick={activate}
                  data-testid="pwa-update-now-btn"
                  className="bg-[#2563EB] hover:bg-[#1E4FCC] text-white"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Aktualizuj teraz
                </Button>
              </div>
            </div>
          )}

          {!isChecking && updateStatus === 'uptodate' && (
            <div
              data-testid="pwa-update-uptodate"
              className="flex items-center gap-2 text-sm text-emerald-700"
            >
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div>Masz zainstalowaną najnowszą wersję aplikacji.</div>
                {lastCheckedLabel && (
                  <div className="text-xs text-gray-500">
                    Ostatnie sprawdzenie: {lastCheckedLabel}
                  </div>
                )}
              </div>
            </div>
          )}

          {!isChecking && updateStatus === 'idle' && (
            <p
              data-testid="pwa-update-hint"
              className="text-xs text-gray-500"
            >
              Tapnij kafelek, aby sprawdzić dostępność aktualizacji.
              {lastCheckedLabel && (
                <> Ostatnie sprawdzenie: {lastCheckedLabel}.</>
              )}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
